import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse } from 'react-bootstrap';
import styled from 'styled-components';
import {
  colorBlue,
  colorRed,
} from '@/utils/palette';
import { left, paddingLeft, marginLeft } from '@/utils/directions';
import { SidebarNavLink, SidebarLinkTitle, SidebarLinkIcon } from './SidebarLink';

const SidebarCategory = ({
  title,
  icon,
  isNew,
  children,
  collapse,
  isOpen,
  onToggle,
  animate,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const resolvedIsOpen = typeof isOpen === 'boolean' ? isOpen : internalIsOpen;

  const handleToggle = () => {
    if (onToggle) {
      onToggle();
      return;
    }

    setInternalIsOpen(prev => !prev);
  };

  return (
    <SidebarCategoryWrap collapse={collapse} $open={resolvedIsOpen}>
      <SidebarCategoryButton
        $selected={resolvedIsOpen}
        as="button"
        type="button"
        aria-expanded={resolvedIsOpen}
        onClick={handleToggle}
      >
        {icon ? <SidebarLinkIcon className={`lnr lnr-${icon}`} /> : ''}
        <SidebarLinkTitle>
          {title}
          {isNew && <SidebarCategoryNew />}
        </SidebarLinkTitle>
        <SidebarCategoryChevron
          className="lnr lnr-chevron-right"
          open={resolvedIsOpen}
          $collapse={collapse}
        />
      </SidebarCategoryButton>
      {collapse || !animate ? resolvedIsOpen && (
        <SidebarSubmenuWrap collapse={collapse} $open={resolvedIsOpen}>
          <SidebarSubmenu collapse={collapse}>
            {children}
          </SidebarSubmenu>
        </SidebarSubmenuWrap>
      ) : (
        <Collapse in={resolvedIsOpen}>
          <SidebarSubmenuWrap collapse={collapse} $open={resolvedIsOpen}>
            <SidebarSubmenu collapse={collapse}>
              {children}
            </SidebarSubmenu>
          </SidebarSubmenuWrap>
        </Collapse>
      )}
    </SidebarCategoryWrap>
  );
};

SidebarCategory.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  collapse: PropTypes.bool.isRequired,
  isNew: PropTypes.bool,
  children: PropTypes.node.isRequired,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  animate: PropTypes.bool,
};

SidebarCategory.defaultProps = {
  icon: '',
  isNew: false,
  isOpen: undefined,
  onToggle: undefined,
  animate: true,
};

export default SidebarCategory;

// region STYLES

const SidebarCategoryButton = styled(SidebarNavLink)`
  width: 100%;

  &:before {
    display: block;
    opacity: ${props => (props.$selected ? 1 : 0)};
  }

  ${props => props.$selected && `
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.12), rgba(255, 255, 255, 0.05));
  `}

  &:hover {
    &:before {
      opacity: 1;
    }
  }
`;

export const SidebarCategoryChevron = styled.span`
  ${marginLeft}: auto;
  ${props => props.open && `
    transform: rotate(90deg);
  `};
  font-size: 10px;
  line-height: 14px;
  color: rgba(255, 255, 255, 0.72);
  opacity: ${props => (props.$collapse ? 0 : 1)};
  transition: opacity 0.18s ease, transform 0.18s ease;
`;

const SidebarSubmenuWrap = styled.div`
  
  @media screen and (min-width: 576px) {
    ${props => props.collapse && `
      position: absolute;
      width: ${props.$open ? '228px' : '0'};
      transition: 0.3s;
      display: ${props.$open ? 'block' : 'none'};
      ${left}: 64px;

      & & {
        position: relative;
        ${left}: 0;
        width: 100%;
        display: ${props.$open ? 'block' : 'none'};

        a {
          ${paddingLeft}: 30px;
        }
      }
    `}
  }
`;

const SidebarSubmenu = styled.ul`
  transition: height 0.5s 0s, padding 0.5s 0s, opacity 0.4s 0.1s;
  padding: 8px 0 3px;
  background-color: rgba(255, 255, 255, 0.03);
  list-style: none;
  margin-top: 2px;
  border-radius: 14px;

  & & {
    margin-bottom: 0;
    padding-bottom: 0;
    padding-top: 4px;
    background-color: ${colorBlue};

    a,
    button {
      ${paddingLeft}: 52px;
      background: transparent !important;

      &.active,
      &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
      }
    }
  }

  a,
  button {
    ${paddingLeft}: 46px;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }
  
  @media screen and (min-width: 576px) {
    ${props => props.collapse && `
        padding: 8px 4px 4px;
        transition: 0s;
        background-color: ${colorBlue};
        box-shadow: 0 12px 26px rgba(10, 21, 37, 0.18);
    `}
  }
`;

const SidebarCategoryWrap = styled.li`
  
  ${props => props.collapse && `
  
    &:hover {
      
      ${SidebarCategoryButton} {
        background: rgba(255, 255, 255, 0.08);
      }

      ${SidebarCategoryChevron} {
        opacity: 1;
      }
    }
  `};
  
  @media screen and (min-width: 576px) {
    
    ${props => props.collapse && `
      & > div {
        position: absolute;
        width: ${props.$open ? '228px' : '0'};
        transition: .3s;
        display: ${props.$open ? 'block' : 'none'};
        left: 64px;
        background: ${colorBlue};
        border-radius: 16px;
        
        a,
        button {
          width: 100%;
          ${paddingLeft(props)}: 12px;
        }
      }
      
      & & > div {
        position: relative;
        min-width: 185px;
        left: 0;
        width: 100%;
        display: ${props.$open ? 'block' : 'none'};
        background: ${colorBlue};
        
        a,
        button {
          ${paddingLeft(props)}: 30px;
        }
      }
    `}
  }
`;

const SidebarCategoryNew = styled.span`
  height: 6px;
  width: 6px;
  border-radius: 50%;
  top: -3px;
  display: block;
  background: ${colorRed};
  ${marginLeft}: 5px;
`;

// endregion
